import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import moment from 'moment';
import { motion } from 'framer-motion';
import {
  FiSearch, FiClock, FiRefreshCw, FiMessageSquare,
  FiThumbsUp, FiThumbsDown, FiAlertTriangle, FiX,
} from 'react-icons/fi';
import '../commissionscan/CommissionScan.css';
import '../homeowners/homeowners.css';
import PreviewIcon from '../../assets/images/ViewDetailsIcon.svg';
import '../../styles/QuoteProposal.css';
import '../../styles/VehiclePrefillHistory.css';
import { useToast } from '../../context/ToastContext';
const PAGE_SIZE  = 10;
const getToken   = () => localStorage.getItem('toolboxToken') || '';

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  Success: { label: 'Success', color: '#34d399' },
  Failed: { label: 'Failed', color: '#f87171' },
  'Not Found': { label: 'Not Found', color: '#9ca3af' },
};

interface HistoryRow {
  transactionId: string;
  userName: string;
  insuredName: string;
  created: string;
  status: string;
  updatedInAMS: string;
  isLiked: boolean | null;
  totalCount: number;
  responseJson: string;
  selectedJsonData: string;
}

interface PreviewVehicle {
  VinNumber: string;
  VehicleType: string;
  VehicleManufacturer: string;
  check: boolean;
}

const VehiclePrefillHistory: React.FC = () => {
  const [rows, setRows]             = useState<HistoryRow[]>([]);
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState('');
  const [page, setPage]             = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  /* Preview modal */
  const [previewRow, setPreviewRow]           = useState<HistoryRow | null>(null);
  const [previewLoading, setPreviewLoading]   = useState(false);
  const [previewVehicles, setPreviewVehicles] = useState<PreviewVehicle[]>([]);
  const [previewUpdatedInAMS, setPreviewUpdatedInAMS] = useState('');

  /* Feedback popup */
  const [feedbackRowId, setFeedbackRowId]     = useState<string | null>(null);
  const [feedbackText, setFeedbackText]       = useState('');
  const [feedbackRating, setFeedbackRating]   = useState<boolean | null>(null);
  const [feedbackAnchor, setFeedbackAnchor]   = useState<{ top: number; right: number } | null>(null);
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackError, setFeedbackError]     = useState('');
  const feedbackRef = useRef<HTMLDivElement>(null);

  /* Toast */
  const { showSuccess, showError } = useToast();

  /* ── Fetch history ── */
  const fetchData = useCallback(async (pageNum: number) => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${(process.env.REACT_APP_DataFabric_Url || '')}/api/vehicle/getvehiclehistory?pageNumber=${pageNum}&recordCount=${PAGE_SIZE}`,
        { headers: { Authorization: `Bearer ${getToken()}` } },
      );
      const data: HistoryRow[] = Array.isArray(res.data) ? res.data : [];
      setRows(data);
      setTotalCount(data.length > 0 ? (data[0].totalCount ?? data.length) : 0);
    } catch {
      showError('Error loading history.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(page); }, [fetchData, page]);
  useEffect(() => { setPage(1); }, [search]);

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter(r =>
      r.userName?.toLowerCase().includes(q) ||
      r.insuredName?.toLowerCase().includes(q) ||
      r.status?.toLowerCase().includes(q) ||
      String(r.updatedInAMS ?? '').toLowerCase().includes(q),
    );
  }, [rows, search]);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const pageList = Array.from({ length: totalPages }, (_, i) => i + 1)
    .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
    .reduce<(number | '…')[]>((acc, p, idx, arr) => {
      if (idx > 0 && (p as number) - (arr[idx - 1] as number) > 1) acc.push('…');
      acc.push(p);
      return acc;
    }, []);

  /* ── Preview ── */
  const openPreview = useCallback((row: HistoryRow) => {
    setPreviewRow(row);
    setPreviewLoading(true);
    setPreviewUpdatedInAMS(row.updatedInAMS || '');

    try {
      const responseJson = row.responseJson ? JSON.parse(row.responseJson) : {};
      const master: any[] = responseJson.VehiclePrefillMaster || [];
      const selected: any[] = row.selectedJsonData ? JSON.parse(row.selectedJsonData) : [];
      const selectedVins = new Set(selected.map((s: any) => s.vinNumber));

      setPreviewVehicles(master.map((item: any) => ({
        VinNumber: item.VinNumber,
        VehicleType: item.VehicleType,
        VehicleManufacturer: item.VehicleManufacturer,
        check: selectedVins.has(item.VinNumber),
      })));
    } catch {
      setPreviewVehicles([]);
    }

    setTimeout(() => setPreviewLoading(false), 300);
  }, []);

  /* ── Feedback ── */
  const openFeedback = useCallback((row: HistoryRow, e: React.MouseEvent<HTMLButtonElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setFeedbackRowId(row.transactionId);
    setFeedbackText('');
    setFeedbackRating(row.isLiked ?? null);
    setFeedbackError('');
    setFeedbackAnchor({ top: rect.bottom + 8, right: window.innerWidth - rect.right });
  }, []);

  const closeFeedback = useCallback(() => {
    setFeedbackRowId(null); setFeedbackAnchor(null);
    setFeedbackText(''); setFeedbackRating(null); setFeedbackError('');
  }, []);

  const saveRating = useCallback((id: string, isLiked: boolean | null, feedback = '') => {
    axios.post(
      `${(process.env.REACT_APP_DataFabric_Url || '')}/api/vehicle/isliked`,
      { TransactionId: id, Feedback: feedback, IsLiked: isLiked },
      { headers: { Authorization: `Bearer ${getToken()}` } },
    ).then(() => showSuccess('Feedback submitted successfully!')).catch(() => {});
  }, [showSuccess]);

  const onThumbsUp = useCallback(() => {
    if (!feedbackRowId) return;
    const cur = rows.find(r => r.transactionId === feedbackRowId);
    const next: boolean | null = cur?.isLiked === true ? null : true;
    setFeedbackRating(next);
    setRows(prev => prev.map(r => r.transactionId === feedbackRowId ? { ...r, isLiked: next } : r));
    saveRating(feedbackRowId, next, feedbackText);
    closeFeedback();
  }, [feedbackRowId, rows, feedbackText, saveRating, closeFeedback]);

  const onThumbsDown = useCallback(() => {
    if (!feedbackRowId) return;
    const cur = rows.find(r => r.transactionId === feedbackRowId);
    const next: boolean | null = cur?.isLiked === false ? null : false;
    setFeedbackRating(next);
    setRows(prev => prev.map(r => r.transactionId === feedbackRowId ? { ...r, isLiked: next } : r));
    saveRating(feedbackRowId, next, feedbackText);
    closeFeedback();
  }, [feedbackRowId, rows, feedbackText, saveRating, closeFeedback]);

  const onSubmitFeedback = useCallback(() => {
    if (!feedbackRowId) return;
    if (!feedbackText.trim()) { setFeedbackError('Please enter your feedback'); return; }
    if (feedbackText.length > 250) { setFeedbackError('Max 250 characters'); return; }
    setFeedbackError('');
    setFeedbackSubmitting(true);
    axios.post(
      `${(process.env.REACT_APP_DataFabric_Url || '')}/api/vehicle/feedback`,
      { TransactionId: feedbackRowId, Feedback: feedbackText },
      { headers: { Authorization: `Bearer ${getToken()}` } },
    )
      .then(() => { showSuccess('Feedback submitted successfully!'); })
      .catch(() => {})
      .finally(() => { setFeedbackSubmitting(false); closeFeedback(); });
  }, [feedbackRowId, feedbackText, closeFeedback, showSuccess]);

  useEffect(() => {
    if (!feedbackRowId) return;
    const handler = (e: MouseEvent) => {
      if (feedbackRef.current && !feedbackRef.current.contains(e.target as Node)) closeFeedback();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [feedbackRowId, closeFeedback]);

  const formatDate = (iso: string) => {
    if (!iso) return '—';
    const local = moment.utc(iso).local();
    return `${local.format('MM/DD/YYYY')} ${local.format('HH:mm')}`;
  };

  /* ════════════ RENDER ════════════ */
  return (
    <>
      <motion.div className="qp-history" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
        <div className="qp-history__header">
          <h3 className="qp-history__title vehicle-prefill-history__title-flush">Vehicle Prefill History</h3>
          <div className="vehicle-prefill-history__header-actions">
            <div className="cs-client-search__input-wrap vehicle-prefill-history__search-wide">
              <FiSearch size={13} className="cs-client-search__icon" />
              <input className="cs-client-search__input" type="text" placeholder="Search…" value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <motion.button className="csh-refresh-btn" onClick={() => fetchData(page)} whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
              <FiRefreshCw size={13} className={loading && rows.length > 0 ? 'csh-spin' : ''} />
              Refresh
            </motion.button>
          </div>
        </div>

        <div className="qp-history__body">
          {loading ? (
            <div className="qp-history-loader vehicle-prefill-history__loader">
              <div className="qp-history-loader__orb">
                <div className="qp-history-loader__ring qp-history-loader__ring--1" />
                <div className="qp-history-loader__ring qp-history-loader__ring--2" />
                <div className="qp-history-loader__core"><FiSearch size={16} /></div>
              </div>
              <div className="qp-history-loader__text">
                <p className="qp-history-loader__title">Loading History</p>
                <p className="qp-history-loader__sub">Fetching vehicle prefill records<span className="qp-init-loader__dots"><span>.</span><span>.</span><span>.</span></span></p>
              </div>
            </div>
          ) : filtered.length === 0 ? (
            <div className="qp-empty-state">
              <FiClock size={28} />
              <p>{search ? 'No records match your search.' : 'No vehicle prefill searches yet.'}</p>
            </div>
          ) : (
            <div className="qp-table-wrap glass-surface">
              <table className="qp-table">
                <thead>
                  <tr>
                    <th className="qp-table__th">User</th>
                    <th className="qp-table__th">Insured Name</th>
                    <th className="qp-table__th">Submitted Date</th>
                    <th className="qp-table__th vehicle-prefill-history__center">Status</th>
                    <th className="qp-table__th vehicle-prefill-history__center">Update In AMS</th>
                    <th className="qp-table__th vehicle-prefill-history__center">Action</th>
                    <th className="qp-table__th vehicle-prefill-history__center">Feedback</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((row, i) => (
                    <tr key={row.transactionId ?? i} className="qp-table__row">
                      <td className="qp-table__td">{row.userName || '—'}</td>
                      <td className="qp-table__td">{row.insuredName || '—'}</td>
                      <td className="qp-table__td vehicle-prefill-history__nowrap">{formatDate(row.created)}</td>
                      <td className="qp-table__td vehicle-prefill-history__center">
                        <span className="qp-status-pill" style={{ color: (STATUS_CONFIG[row.status] || STATUS_CONFIG.Failed).color }}>
                          {(STATUS_CONFIG[row.status] || { label: row.status }).label}
                        </span>
                      </td>
                      <td className="qp-table__td vehicle-prefill-history__center">
                        {row.updatedInAMS
                          ? <span className="vehicle-prefill-history__ams-badge">{row.updatedInAMS}</span>
                          : <span className="vehicle-prefill-history__muted-sm">—</span>
                        }
                      </td>
                      <td className="qp-table__td vehicle-prefill-history__center">
                        {row.status === 'Success' ? (
                          <button className="qp-table__action-btn" title="Preview" onClick={() => openPreview(row)}>
                            <img src={PreviewIcon} alt="Preview" width={30} height={30} />
                          </button>
                        ) : (
                          <span className="vehicle-prefill-history__muted-sm">Not Available</span>
                        )}
                      </td>
                      <td className="qp-table__td vehicle-prefill-history__center">
                        <button className="qp-table__action-btn vehicle-prefill-history__feedback-btn" title="Feedback" onClick={e => openFeedback(row, e)}>
                          <FiMessageSquare size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalCount > 0 && (
            <div className="qp-pagination">
              <span className="qp-pagination__info">{(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, totalCount)} of {totalCount}</span>
              <div className="qp-pagination__controls">
                <button className="qp-pagination__btn" disabled={page === 1} onClick={() => setPage(1)} title="First">«</button>
                <button className="qp-pagination__btn" disabled={page === 1} onClick={() => setPage(p => p - 1)} title="Previous">‹</button>
                {pageList.map((p, i) =>
                  p === '…'
                    ? <span key={`e-${i}`} className="qp-pagination__ellipsis">…</span>
                    : <button key={p} className={`qp-pagination__btn${page === p ? ' qp-pagination__btn--active' : ''}`} onClick={() => setPage(p as number)}>{p}</button>
                )}
                <button className="qp-pagination__btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)} title="Next">›</button>
                <button className="qp-pagination__btn" disabled={page === totalPages} onClick={() => setPage(totalPages)} title="Last">»</button>
              </div>
            </div>
          )}
        </div>
      </motion.div>

      {/* ── Feedback popup (portalled) ── */}
      {feedbackRowId && feedbackAnchor && ReactDOM.createPortal(
        <div ref={feedbackRef} className="csfb-popup" style={{ top: feedbackAnchor.top, right: feedbackAnchor.right }}>
          <h4 className="csfb-title">Feedback</h4>
          <textarea className="csfb-textarea" placeholder="Enter your feedback here" value={feedbackText} onChange={e => setFeedbackText(e.target.value)} rows={4} />
          {feedbackError && <span className="vehicle-prefill-history__field-error">{feedbackError}</span>}
          <div className="csfb-rating">
            <button className={`csfb-rating-btn${feedbackRating === true ? ' csfb-rating-btn--up' : ''}`} onClick={onThumbsUp} title="Thumbs up"><FiThumbsUp size={20} /></button>
            <button className={`csfb-rating-btn${feedbackRating === false ? ' csfb-rating-btn--down' : ''}`} onClick={onThumbsDown} title="Thumbs down"><FiThumbsDown size={20} /></button>
          </div>
          <div className="csfb-actions">
            <button className="csfb-btn-cancel" onClick={closeFeedback}>Cancel</button>
            <button className="csfb-btn-submit" disabled={feedbackSubmitting} onClick={onSubmitFeedback}>{feedbackSubmitting ? 'Submitting…' : 'Submit'}</button>
          </div>
        </div>,
        document.body,
      )}

      {/* ── Preview modal (portalled) ── */}
      {previewRow && ReactDOM.createPortal(
        <div
          className="vehicle-prefill-history__modal-overlay"
          onClick={() => setPreviewRow(null)}
        >
          <div className="ho-modal vehicle-prefill-history__modal-width" onClick={e => e.stopPropagation()}>
            <div className="ho-modal__header">
              <div className="ho-modal__title-row">
                <h3 className="ho-modal__title">Vehicle Prefill Details</h3>
              </div>
              <button className="ho-modal__close" onClick={() => setPreviewRow(null)}><FiX size={16} /></button>
            </div>

            <div className="ho-modal__body">
              {previewLoading ? (
                <div className="qp-empty-state vehicle-prefill-history__empty-pad"><FiRefreshCw size={22} className="ho-spin" /><p>Loading…</p></div>
              ) : (
                <>
                  <div className="vehicle-prefill-history__update-row">
                    <span className="vehicle-prefill-history__update-label">
                      Update In AMS:{' '}
                      <strong className="vehicle-prefill-history__update-value">{previewUpdatedInAMS || '—'}</strong>
                    </span>
                  </div>
                  {previewVehicles.length === 0 ? (
                    <div className="qp-empty-state vehicle-prefill-history__empty-pad"><FiAlertTriangle size={22} /><p>No vehicle data available.</p></div>
                  ) : (
                    <div className="qp-table-wrap glass-surface">
                      <table className="qp-table">
                        <thead>
                          <tr>
                            <th className="qp-table__th vehicle-prefill-history__checkbox-th"></th>
                            <th className="qp-table__th">VIN</th>
                            <th className="qp-table__th">Type</th>
                            <th className="qp-table__th">Make</th>
                          </tr>
                        </thead>
                        <tbody>
                          {previewVehicles.map((v, i) => (
                            <tr key={i} className="qp-table__row">
                              <td className="qp-table__td">
                                <input type="checkbox" checked={v.check} disabled />
                              </td>
                              <td className="qp-table__td vehicle-prefill-history__vin-cell">{v.VinNumber}</td>
                              <td className="qp-table__td">{v.VehicleType}</td>
                              <td className="qp-table__td">{v.VehicleManufacturer}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  <p className="vehicle-prefill-history__footnote">Checked rows were the ones selected and pushed to NowCerts.</p>
                </>
              )}
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
};

export default VehiclePrefillHistory;

